import { useInfiniteQuery } from "@tanstack/react-query";

import { ContentLoading } from "../components/ContentLoading";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { activitySentence } from "./billing-activity";
import { billingQueryKey, getBillingHistory } from "./billing-api";
import { formatDay } from "./billing-format";
import { EmptyCard, PageSection } from "./PageSection";

/**
 * What changed in this customer's billing, newest first, ten at a time (ADR-0073). Opening the
 * tab is audited but never listed here: the list is for changes someone needs to know about.
 */
export function BillingActivity({ customerSlug }: { customerSlug: string }) {
  const history = useInfiniteQuery({
    queryKey: [...billingQueryKey(customerSlug), "history"],
    queryFn: ({ pageParam }) => getBillingHistory(customerSlug, { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
  const events = history.data?.pages.flatMap((page) => page.events) ?? [];
  return (
    <PageSection title="Activity" id="billing-history-title" level="h3">
      {history.isPending ? <ContentLoading label="Loading activity…" /> : null}
      {history.error ? <FieldError>{history.error.message}</FieldError> : null}
      {history.isSuccess && events.length === 0 ? (
        <EmptyCard>Nothing has changed yet.</EmptyCard>
      ) : null}
      {events.length > 0 ? (
        <ol
          className="divide-y divide-muted rounded-xl bg-card shadow-card"
          aria-label="Billing activity"
        >
          {events.map((event) => (
            <li
              key={event.id}
              className="grid gap-1 px-6 py-3.5 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-4"
            >
              <span className="text-sm text-muted-foreground">{formatDay(event.createdAt)}</span>
              <span>
                <span className="block font-semibold">{activitySentence(event)}</span>
                {event.reason ? (
                  <span className="block text-sm text-muted-foreground">{event.reason}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {history.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={history.isFetchingNextPage}
            onClick={() => {
              void history.fetchNextPage();
            }}
          >
            {history.isFetchingNextPage ? "Loading…" : "Show more"}
          </Button>
        </div>
      ) : null}
    </PageSection>
  );
}
